import { ChangeEvent, useCallback, useContext, useEffect, useReducer, useState } from "react";

import { Form } from "@/components/Form";
import { Typography } from "@/components/Typography";
import { ListInputSchema } from "@/components/ListInput/types";

import { QREditorContext } from "@/contexts/QREditorContext/QrEditorContext";
import { QREditorContextProps, QRProps } from "@/contexts/QREditorContext/types";

import { qrFeedbackValidation } from "@/utils/validators/qr-feedback";
import { applyQRFormDefaultValues, onBindClick } from "@/utils/formHelper";
import { omittedValues, onBindImage, onBindThemePicker } from "@/utils/qrEditorHelper";

import { NAMESPACES } from "@/internals/constants/namespaces";
import { useTranslation } from "react-i18next";
import {
  FeedbackFormFields,
  QRFeedbackProps,
  onChangeFeedbackFormState,
  onChangeFeedbackFormStateAction,
} from "./types";
import { QRFeedbackCategory } from "@/types/qr";

import "./styles.scss";

const onChangeFeedbackFormStateReducer = (
  state: onChangeFeedbackFormState,
  action: onChangeFeedbackFormStateAction
) => {
  switch (action.type) {
    case "name":
      return { ...state, name: action.value };
    case "phone":
      return { ...state, phone: action.value };
    case "email":
      return { ...state, email: action.value };
    default:
      return state;
  }
};

const formatQRFeedbackFormDefaultValues = (qrData: QRProps<"feedback">) => {
  const form = qrData.data.form;

  return {
    ...qrData.data,
    name: form ? form.name : true,
    phone: form ? form.phone : true,
    email: form ? form.email : true,
  };
};

export function QRFeedback({ data }: QRFeedbackProps) {
  const { t } = useTranslation(NAMESPACES.VALIDATIONS);
  const { qrData, setQrData } = useContext<QREditorContextProps<"feedback">>(QREditorContext);

  const [renderForm, setRenderForm] = useState(false);
  const [feedbackFormState, feedbackFormDispatch] = useReducer(
    onChangeFeedbackFormStateReducer,
    qrData.data.form || { name: true, phone: true, email: true }
  );

  const dataFeedbackWithDefaultValues = useCallback(
    applyQRFormDefaultValues(data, formatQRFeedbackFormDefaultValues(qrData)),
    []
  );

  onBindImage(dataFeedbackWithDefaultValues, "qrFeedbackImage", omittedValues(qrData.data));

  onBindThemePicker(
    dataFeedbackWithDefaultValues,
    "qrFeedbackThemePicker",
    omittedValues(qrData.data)
  );

  onBindClick(
    dataFeedbackWithDefaultValues,
    "qrFeedbackSubmissionForm",
    (event: ChangeEvent<HTMLInputElement>) =>
      feedbackFormDispatch({
        type: event.target.name as FeedbackFormFields,
        value: event.target.checked,
      })
  );

  onBindClick(
    dataFeedbackWithDefaultValues,
    "qrFeedbackCategories",
    (inputs: ListInputSchema[]) => {
      const categories: QRFeedbackCategory[] = inputs.map((input) => ({
        id: input.id,
        name: input.value,
        rating: input.rating,
      }));

      setQrData((prev) => ({
        ...prev,
        data: {
          ...prev.data,
          categories,
        },
      }));
    }
  );

  const onUpdateState = ({ field, value }) => {
    if (field === "file" || field == "" || field == "categoryName") return;

    setQrData(
      {
        ...qrData,
        data: {
          ...qrData.data,
          [field]: value,
        },
        isValidForm: renderForm,
      },
      omittedValues(qrData.data)
    );
  };

  useEffect(() => {
    onUpdateState({ field: "form", value: feedbackFormState });
  }, [renderForm, feedbackFormState]);

  return (
    <div className="qr-feedback">
      <Typography
        Tag="p"
        size="xl"
        classname="u-bold u-spacing-5"
      >
        {data.title}
      </Typography>
      <Form
        fields={dataFeedbackWithDefaultValues}
        schema={qrFeedbackValidation(data, t)}
        onWatchFields={({ field, value }) => onUpdateState({ field, value })}
        isFormValid={(isValidForm) => setRenderForm(isValidForm)}
      />
    </div>
  );
}
